<template>
  <loading
    color="#4e46dc"
    v-model:active="loading"
    transition="disappear"
    :can-cancel="false"
    :is-full-page="true"
  />
  <div class="container mx-auto px-4 sm:py-8 sm:container sm:mx-auto">
    <div v-if="tickets == null && !loading">{{ $t("not_found_order") }}</div>
    <div
      v-if="tickets.length > 0"
      class="slider-container"
      ref="sliderContainer"
    >
      <div v-if="showGroupOption" class="group-toggle">
        <label class="flex items-center gap-2">
          <input type="checkbox" v-model="groupByPriceSchema" />
          <span>{{ $t("group_tickets") }}</span>
        </label>
      </div>
      <p v-if="showGroupOption" class="group-info">{{ $t("group_tickets_info") }}</p>

      <div
        class="slider"
        :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
      >
        <div
          class="slide"
          v-for="(ticket, index) in displayItems"
          :key="index"
        >
          <div class="slide-content">
            <div class="ticket-info shadow-md">
              <div class="maintitlecont">
                <div  v-show="groupByPriceSchema" class="tks-number capitalized" >
                  {{$t("ticket_type")}} {{ currentSlide + 1 }} / {{ totalSlides }}
                </div>
                <div  v-show="!groupByPriceSchema" class="tks-number capitalized" >
                  {{$t("ticket")}} {{ currentSlide + 1 }} / {{ totalSlides }}
                </div>
              </div>
                            
              <p class="mb-1 qty-emphasis" v-if="groupByPriceSchema">
                {{ $t("quantity") }}: {{ ticket.quantity }}
              </p>
              <h2 class="mb-1 font-semibold">{{ ticket.title }}</h2>
              <p class="mb-1" v-if="(ticket.dateVisibility == null || ticket.dateVisibility == 0) && ticket.dateEnd != null">
                {{ $t("access_interval")}}
              </p>
              <p class="mb-1" v-if="ticket.dateVisibility == null || ticket.dateVisibility == 0">
                {{ formatEiDateFull(ticket) }}
              </p>
              <p class="mb-1" v-if="ticket.dateVisibility == 1">
                {{ formatEiDate(ticket.date) }}
              </p>
              <p class="mb-1">
                {{$t("price")}}: {{ ticket.price }}  {{ ticket.currency }}
                <span v-if="ticket.promotion">
                {{ $t("discount") }}: {{ ticket.promotion }}
              </span>
              </p>
            </div>
            <div class="ticket-qr">
              <qrcode :text="ticket.qrCode" class="flex w-32 h-32"/>
            </div>
            <div class="qr-text">{{ ticket.qrCode }}</div>
          </div>
        </div>
      </div>
      <button
        class="slider-prev"
        @click="prevSlide"
        :disabled="currentSlide === 0"
      >
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <button
        class="slider-next"
        @click="nextSlide"
        :disabled="currentSlide === totalSlides - 1"
      >
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import Qrcode from "vue-qrcode-component";
import { formatEiDateFull, formatEiDate } from "../mixins/dateformats";
import Loading from "vue-loading-overlay";

export default {
  props: ["acOrder", "acInstance"],
  components: {
    Qrcode,
    Loading
  },
  data() {
    return {
      loading: false,
      order: null,
      instance: null,
      currentSlide: 0,
      groupByPriceSchema: false,
    };
  },
  methods: {
    formatEiDateFull,
    formatEiDate,
    prevSlide() {
        this.currentSlide = Math.max(0, this.currentSlide - 1);
    },
    nextSlide() {
      this.currentSlide = Math.min(this.currentSlide + 1, this.totalSlides - 1)
    },
  },
  computed: {
    tickets() {
      let tickets = [];
      if (this.order == null) {
        return [];
      }
      let instances = [];
      if (this.instance != null) {
        instances.push(this.instance);
      } else {
        instances = this.order.ceiList;
      }
      for (let instance of instances) {
        for (let cat of instance.categories) {
          for (let t of cat.tickets) {

            tickets.push({
              title: instance.name,
              instanceId: instance.id,
              date: instance.date,
              dateVisibility: instance.dateVisibility,
              dateEnd: instance.dateEnd,
              price: t.fullPrice != null ? t.fullPrice : t.price,
              promotion: t.promotion,
              qrCode: t.barcode,
            });
          }
        }
      }
      tickets.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        if (dateA !== dateB) return dateA - dateB;

        if (a.instanceId !== b.instanceId) {
          return (a.instanceId ?? 0) - (b.instanceId ?? 0);
        }

        if (a.price !== b.price) {
          return (b.price ?? 0) - (a.price ?? 0);
        }

        return String(a.promotion ?? "").localeCompare(String(b.promotion ?? ""));
      });
      return tickets;
    },
    groupedTickets() {
      if (this.tickets.length === 0) return [];

      const groups = new Map();

      for (const t of this.tickets) {
        const key = [
          t.instanceId,
          t.date,
          t.dateEnd,
          t.price,
          t.promotion
        ].join("|");

        if (!groups.has(key)) {
          groups.set(key, {
            title: t.title,
            instanceId: t.instanceId,
            date: t.date,
            dateVisibility: t.dateVisibility,
            dateEnd: t.dateEnd,
            price: t.price,
            promotion: t.promotion,
            qrCode: t.qrCode,
            quantity: 1,
          });
        } else {
          groups.get(key).quantity += 1;
        }
      }

      return Array.from(groups.values());
    },
    displayItems() {
      return this.groupByPriceSchema ? this.groupedTickets : this.tickets;
    },
    totalSlides() {
      return this.displayItems.length;
    },
    showGroupOption() {
      const hasMoreThanSix = this.tickets.length > 6;
      const hasGroupable = this.groupedTickets.some(g => g.quantity >= 2);
      return hasMoreThanSix && hasGroupable;
    },
  },
  watch: {
    groupByPriceSchema() {
      this.currentSlide = 0;
    }
  },
  async created() {
    if (this.acOrder != null) {
      this.order = this.acOrder;
      this.instance = this.acInstance;
      if (this.showGroupOption) {
        this.groupByPriceSchema = true;
      }
    } else if (this.$route.query.code != null) {
      this.loading = true;
      let orderResp = await this.$store.dispatch(
        "getOrder",
        this.$route.query.code
      );
      if (orderResp.result == 1) {
        this.order = orderResp.data;
      }
      this.loading = false;
      if (this.showGroupOption) {
        this.groupByPriceSchema = true;
      }
    }
  },
  mounted() {
    if (this.tickets.length > 0) {
      const sliderContainer = this.$refs.sliderContainer;
      let touchStartX = 0;
      let touchEndX = 0;

      sliderContainer.addEventListener("touchstart", (event) => {
        touchStartX = event.touches[0].clientX;
        touchEndX = touchStartX;
      });

      sliderContainer.addEventListener("touchmove", (event) => {
        touchEndX = event.touches[0].clientX;
      });

      sliderContainer.addEventListener("touchend", () => { 
        const diffX = touchEndX - touchStartX;
        if (diffX > 50) {
          this.prevSlide();
        } else if (diffX < -50) {
          this.nextSlide();
        }
      });
    }
  },
};
</script>

<style scoped>
.slider-container {
  position: relative;
  max-width: 100% !important;
  margin: 0 auto;
  overflow: hidden;
}

.slider {
  display: flex;
  width: 100%;
  transition: transform 0.3s ease-in-out;
}

.slide {
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 30px;
  max-width: 800px;
}

.ticket-info {
  text-align: left;
  padding: 5px 20px 20px;
  background-color: #5f2eea;
  color: #fff;
  width: 100%;
  border-radius: 10px;
}

.ticket-qr {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slider-prev,
.slider-next {
  position: absolute;
  top: 54%;
  transform: translateY(calc(-54% -15px));
  padding: 8px 16px;
  font-size: 14px;
  background-color: #5f2eea;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.slider-prev {
  left: 16px;
}

.slider-next {
  right: 16px;
}


.ticket-qr {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ticket-qr img {
  max-width: 120px;
}

.tks-number {
  font-size: 14px;
  font-weight: bold;
  padding: 0px 0 10px;
}

.maintitlecont {
  padding: 10px 0;
}

.qr-text {
  font-size: 12px; /* Small font size */
  color: #666; /* Optional: Gray color for better readability */
  text-align: center;
  word-break: break-word; /* Ensure long QR codes wrap properly */
}

.qty-emphasis {
  font-weight: 700;
  font-size: 16px;
  color: #ffffff;
  border-radius: 6px;
  display: inline-block;
}

@media (max-width: 767px) {
  .slider-container {
    max-width: 90%;
  }
  .maintitlecont {
    padding: 0;
  }

  .slider-container {
    margin-top: 40px;
  }
}

.group-toggle {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
}

.group-toggle input {
  accent-color: #5f2eea;
}

.group-toggle input:focus,
.group-toggle input:focus-visible {
  outline: none;
  box-shadow: none;
}

.group-info {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
}
</style>
